import {
  type AxiosLikeFetchResponse,
  createAxiosLikeFetch,
} from '@devhacker/shared/libs/fetch/createAxiosLikeFetch';
import { createLoggerInterceptors } from '@devhacker/shared/libs/logger';
import { REACTIONS_API_URL } from '~/constants/config';
import { API_TIMEOUT } from '@devhacker/shared/config/general';
import { authErrorHandler } from '~/utils/errorHandlerAuth';
import { authRequestInterceptor } from '~/utils/interceptorsRequestAuth';
import type { ReactionFromServer, TReactionType } from '~/types/typings';
import { useXUserId } from '@devhacker/shared/composables/useXUserId';

export interface ReactionsApi {
  fetchReactions: (ids: number[]) => Promise<
    AxiosLikeFetchResponse<{
      data: ReactionFromServer[];
      success: boolean;
    }>
  >;
  updateReaction: (
    id: number,
    user_state: TReactionType,
    isHealth: boolean,
  ) => Promise<
    AxiosLikeFetchResponse<{
      data: ReactionFromServer;
      success: boolean;
    }>
  >;
  updateLike: (id: number) => Promise<
    AxiosLikeFetchResponse<{
      data: ReactionFromServer;
      success: boolean;
    }>
  >;
  updateDislike: (id: number) => Promise<
    AxiosLikeFetchResponse<{
      data: ReactionFromServer;
      success: boolean;
    }>
  >;
}

export const createReactionsApi = ({ $logger }: any): ReactionsApi => {
  const loggerInterceptors = createLoggerInterceptors($logger, 'reactions');
  const reactionsFetch = createAxiosLikeFetch({
    baseURL: REACTIONS_API_URL,
    timeout: API_TIMEOUT,
    parseResponse: JSON.parse, // без этого API может вернуть 200 код и html
    onRequest: [
      loggerInterceptors.onRequest,
      addXUserIdHeader,
      (config) => authRequestInterceptor(config),
    ],
    onRequestError: [
      loggerInterceptors.onRequestError,
      (config) => authErrorHandler(config, 'reactions'),
    ],
    onResponse: loggerInterceptors.onResponse,
    onResponseError: [
      loggerInterceptors.onResponseError,
      (config) => authErrorHandler(config, 'reactions'),
    ],
  });

  return {
    fetchReactions: (ids) => {
      const url = '/api/v1/posts';
      const params = { post_ids: ids.join() };
      return reactionsFetch(url, { params });
    },
    updateReaction: (post_id: number, user_state: TReactionType, isHealth: boolean = false) => {
      const url = '/api/v1/posts';
      const body = {
        post_id,
        user_state,
        ...(isHealth && { post_type: 'health' }),
      };
      return reactionsFetch(url, { method: 'post', body });
    },
    // используется только в рецептах
    updateLike: (id: number) => {
      const url = '/api/v1/posts';
      const body = {
        post_id: id,
        user_state: 'like',
      };
      return reactionsFetch(url, { method: 'post', body });
    },
    // используется только в рецептах
    updateDislike: (id: number) => {
      const url = '/api/v1/posts';
      const body = {
        post_id: id,
        user_state: 'dislike',
      };
      return reactionsFetch(url, { method: 'post', body });
    },
  };
};

function addXUserIdHeader(config: any) {
  if (import.meta.server) return;
  const xUserId = useXUserId();
  config.options.headers = new Headers(config.options.headers);
  // TODO: заменить X-UserId на X-User-Id после релиза избы
  config.options.headers.set('X-UserId', xUserId);
}
